import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, Trash2, MessageSquare, CheckCircle, X, Sparkles, ChevronRight, UserPlus, Link2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function TherapistNotifications() {
  const [activityNotifications, setActivityNotifications] = useState([]);
  const [onboardingNotifications, setOnboardingNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const [activityRes, countRes, onboardingRes] = await Promise.all([
        axios.get(`${API}/therapist/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/therapist/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/notifications/onboarding`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { notifications: [] } }))
      ]);
      
      setActivityNotifications(activityRes.data);
      setUnreadCount(countRes.data.unread_count);
      
      // Filter onboarding notifications to only show non-dismissed ones
      const onboarding = onboardingRes.data.notifications || [];
      setOnboardingNotifications(onboarding.filter(n => !n.dismissed));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Fetch on mount and poll periodically
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/therapist/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/therapist/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/therapist/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const wasUnread = activityNotifications.find(n => n.id === notificationId && !n.read);
      setActivityNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const dismissOnboardingNotification = async (notificationId, e) => {
    e?.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/onboarding/${notificationId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const handleActivityNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Feb 2026 — parent_link_* notifications don't carry a child_id; the
    // useful landing is the clinical child profile if we have one, or
    // just close the dropdown (parent-connect UX is on the child page).
    const t = notification.notification_type;
    const details = notification.details || {};
    if (t === 'diary_reminder') {
      navigate(details.link || '/diary');
      setIsOpen(false);
      return;
    }
    const childTarget = details.clinical_child_id || details.child_id;
    if (childTarget) {
      // Canonical URL is /children/:id (plural). The old /therapist/child/:id
      // route no longer resolves — it's what caused the "click does nothing"
      // production bug reported Feb 2026.
      navigate(`/children/${childTarget}`);
      setIsOpen(false);
      return;
    }
    if (t === 'parent_link_request' || t === 'parent_link_accepted' || t === 'parent_joined') {
      // No child anchor — take the therapist to their caseload where the
      // parent-link admin UI lives.
      navigate('/');
      setIsOpen(false);
    }
  };

  const handleOnboardingNotificationClick = (notification) => {
    dismissOnboardingNotification(notification.id);
    if (notification.action_path) {
      navigate(notification.action_path);
      setIsOpen(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'activity_completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'parent_comment':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'parent_link_request':
        return <UserPlus className="w-4 h-4 text-amber-500" />;
      case 'parent_link_accepted':
      case 'parent_joined':
        return <Link2 className="w-4 h-4 text-emerald-500" />;
      case 'diary_reminder':
        return <Clock className="w-4 h-4 text-amber-600" />;
      default:
        return <Bell className="w-4 h-4 text-stone-500" />;
    }
  };

  const getNotificationMessage = (notification) => {
    const { notification_type, details } = notification;
    const childName = details?.child_name || 'A child';
    const parentName = details?.parent_name || 'A parent';
    
    switch (notification_type) {
      case 'diary_reminder': {
        const items = details?.items || [];
        if (details?.is_summary) {
          return (
            <div data-testid="diary-reminder-notification">
              <span className="font-medium">Today's diary</span> — {items.length} item{items.length !== 1 ? 's' : ''}
              <p className="text-xs text-stone-500 mt-1">{items.slice(0, 3).map((i) => i.title).join(' · ')}</p>
            </div>
          );
        }
        return (
          <div data-testid="diary-reminder-notification">
            Diary reminder: <span className="font-medium">{details?.title}</span>
            <p className="text-xs text-stone-500 mt-1">
              Due {details?.due_date}{details?.due_time ? ` at ${details.due_time}` : ''}
              {details?.child_name ? ` · ${details.child_name}` : ''} — tap to open
            </p>
          </div>
        );
      }
      case 'activity_completed':
        return (
          <div>
            <span className="font-medium">{parentName}</span> completed an activity for <span className="font-medium">{childName}</span>
            {details?.parent_comment && (
              <p className="text-xs text-stone-500 mt-1 italic">"{details.parent_comment}"</p>
            )}
          </div>
        );
      case 'parent_comment':
        return (
          <div>
            <span className="font-medium">{parentName}</span> commented on {childName}'s activity
            <p className="text-xs text-stone-500 mt-1 italic">"{details?.comment}"</p>
          </div>
        );
      case 'parent_link_request':
        return (
          <div>
            <span className="font-medium">{parentName}</span> asked to link with you on PlayFirst Parent.
            <p className="text-xs text-stone-500 mt-1">
              {details?.parent_email || 'They\'ll appear in your Parent Links inbox — accept to start sharing focus goals.'}
            </p>
          </div>
        );
      case 'parent_link_accepted':
        return (
          <div>
            <span className="font-medium">{parentName}</span> accepted your PlayFirst Parent link for{' '}
            <span className="font-medium">{childName}</span>.
            <p className="text-xs text-stone-500 mt-1">
              Any goals you star will now steer their daily play ideas.
            </p>
          </div>
        );
      case 'parent_joined':
        return (
          <div>
            <span className="font-medium">{parentName}</span> joined your practice
            {childName !== 'A child' ? <> for <span className="font-medium">{childName}</span></> : null}.
            {details?.parent_email && (
              <p className="text-xs text-stone-500 mt-1">{details.parent_email}</p>
            )}
          </div>
        );
      default:
        // Fallback for unknown / new notification types — render the
        // notification's own title + message so the user actually sees
        // SOMETHING useful, not a meaningless 'New notification' string.
        // Some notification types (parent_link_*, etc.) put their body
        // inside `details.message` rather than top-level `message` — try
        // both so no legit notification ever looks blank.
        {
          const displayTitle = notification.title || details?.title || '';
          const displayMsg = notification.message || details?.message || '';
          return (
            <div>
              {displayTitle && (
                <span className="font-medium">{displayTitle}</span>
              )}
              {displayMsg && (
                <p className="text-xs text-stone-500 mt-1 line-clamp-2">
                  {displayMsg}
                </p>
              )}
              {!displayTitle && !displayMsg && (
                <span className="text-stone-500">You have a new update on PlayFirst — open your dashboard to see it.</span>
              )}
            </div>
          );
        }
    }
  };

  const totalBadgeCount = unreadCount + onboardingNotifications.length;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {totalBadgeCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500"
            >
              {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b px-2 pt-2">
            <TabsList className="w-full grid grid-cols-2 h-9">
              <TabsTrigger value="activity" className="text-xs relative">
                Parent Updates
                {unreadCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-4 text-center">
                    {unreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="getting-started" className="text-xs relative">
                Getting Started
                {onboardingNotifications.length > 0 && (
                  <span className="ml-1 bg-violet-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-4 text-center">
                    {onboardingNotifications.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="activity" className="m-0 max-h-80 overflow-y-auto">
            {unreadCount > 0 && (
              <div className="flex justify-end px-3 py-2 border-b">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              </div>
            )}
            
            {activityNotifications.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-stone-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                <p>No parent updates yet</p>
                <p className="text-xs mt-1">You'll see activity completions and comments here</p>
              </div>
            ) : (
              activityNotifications.slice(0, 15).map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 px-3 py-3 cursor-pointer hover:bg-stone-50 border-b last:border-b-0 ${
                    !notification.read ? 'bg-blue-50/50' : ''
                  }`}
                  onClick={() => handleActivityNotificationClick(notification)}
                >
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      {getNotificationMessage(notification)}
                    </div>
                    <p className="text-xs text-stone-400 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-stone-400 hover:text-red-500"
                      onClick={(e) => deleteNotification(notification.id, e)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
          
          <TabsContent value="getting-started" className="m-0 max-h-80 overflow-y-auto">
            {onboardingNotifications.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-stone-500">
                <Sparkles className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                <p>All caught up!</p>
                <p className="text-xs mt-1">You've completed all getting started tasks</p>
              </div>
            ) : (
              onboardingNotifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleOnboardingNotificationClick(notif)}
                  className="flex items-start gap-3 px-3 py-3 cursor-pointer hover:bg-violet-50 border-b last:border-b-0"
                >
                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-stone-900">{notif.title}</h3>
                    <p className="text-xs text-stone-600 mt-0.5 line-clamp-2">
                      {notif.message?.split('\n')[0]}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-violet-400 flex-shrink-0 mt-1" />
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
